/* eslint-disable no-underscore-dangle */
import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { BreederListGetLittersQuery, BreederListGetLittersQueryVariables } from '@gql/graphql';
import { useMemo } from 'react';
import { useLitterOrderBuilder } from '../../../LitterListPageController/api/useLitterOrderBuilder';
import { useSearch } from '../../useSearch';
import { useLitterWhereBuilder } from './useLitterWhereBuilder';

export const BREEDER_LIST_GET_LITTERS = graphql(`
  query BreederListGetLitters(
    $where: LitterWhereInput
    $orderBy: [LitterOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $litterCardInfoTranslationsWhere: LitterTranslationWhereInput
    $litterCardInfoBreedTranslationsWhere: DogBreedTranslationWhereInput
    $litterCardInfoImgsTake: Int!
  ) {
    findManyLitter(where: $where, orderBy: $orderBy, take: $take) {
      ...LitterCardInfo
    }
    aggregateLitter(where: $where) {
      _count {
        _all
      }
    }
  }
`);

export interface UseGetLittersParams {
  forceSkip?: boolean;
}

export const useGetLitters = (params: UseGetLittersParams) => {
  const { forceSkip = false } = params;

  const { language } = useRouter();
  const { valuesFromSearch } = useSearch();
  const { litterWhereBuilder } = useLitterWhereBuilder();
  const { litterOrderBuilder } = useLitterOrderBuilder();

  const variables: BreederListGetLittersQueryVariables = useMemo(() => {
    return {
      where: litterWhereBuilder(valuesFromSearch),
      orderBy: litterOrderBuilder(),
      litterCardInfoTranslationsWhere: { language: { equals: language } },
      litterCardInfoBreedTranslationsWhere: { language: { equals: language } },
      litterCardInfoImgsTake: 1,
      litterCardInfoLittersWhere: {
        archived: { equals: false },
        adminValidated: { equals: true },
      },
    } as BreederListGetLittersQueryVariables;
  }, [litterWhereBuilder, litterOrderBuilder, language, valuesFromSearch]);

  const { data, error, loading, fetchMore, refetch } = useQuery<
    BreederListGetLittersQuery,
    BreederListGetLittersQueryVariables
  >(BREEDER_LIST_GET_LITTERS, {
    variables,
    notifyOnNetworkStatusChange: true,
    skip: forceSkip,
  });

  return {
    litters: data?.findManyLitter ?? [],
    count: data?.aggregateLitter?._count?._all ?? 0,
    // eslint-disable-next-line no-underscore-dangle
    littersError: error,
    littersLoading: loading,
    littersVariables: variables,
    littersFetchMore: fetchMore,
    littersRefetch: refetch,
  };
};
